<template>
  <CommonPageShell
    :title="text.page.title"
    :subtitle="text.page.subtitle"
    :actions="headerActions"
    @action="onHeaderAction"
  > 
    <template #metrics>
      <section class="metrics-grid invoices-kpis-grid" :style="kpiGridStyle">
        <InvoicesKpiCard
          v-for="metric in kpiCards"
          :key="metric.title"
          :title="metric.title"
          :value="metric.value"
          :value-meta-items="metric.valueMetaItems"
          :value-meta-lines="metric.valueMetaLines"
          :value-segments="metric.valueSegments"
          :value-scale="metric.valueScale"
          :icon="metric.icon"
          :icon-bg="metric.iconBg"
          :icon-color="metric.iconColor"
        />
      </section>
    </template>

    <template #toolbar>
      <InvoicesToolbar
        :filter-options="filterOptions"
        :labels="text.toolbar"
        @search-change="handleToolbarSearch"
        @filters-change="handleToolbarFilters"
        @apply="applyToolbarFilters"
      />
    </template>

    <template #tabs>
      <StatusTabs
        :tabs="statusTabs"
        :active-tab="activeStatusTab"
        @select="selectStatusTab"
      />
    </template>

    <template #table>
      <InvoicesTable
        :rows="paginatedRows"
        :loading="tableLoading"
        :pagination="invoicesPagination"
        :labels="text.table"
        :loading-text="text.table.loading"
        :empty-text="text.table.empty"
        :sort-field="sortField"
        :sort-direction="sortDirection"
        @view-invoice="viewInvoice"
        @pay-invoice="payInvoice"
        @download-files="downloadFiles"
        @download-xml="downloadXml"
        @download-pdf="downloadPdf"
        @upload-receipt="uploadReceipt"
        @clone-invoice="cloneInvoice"
        @page-change="handlePageChange"
        @sort-change="handleSortChange"
      />
    </template>

    <template #modal>
      <ExportFormatModal
        :show="isInvoiceExportModalOpen"
        :filters="exportFilters"
        :search-term="appliedToolbarSearch"
        :page-customer-ids="currentPageInvoiceIds"
        :text-catalog="text"
        :export-service="exportService"
        file-name-prefix="facturas"
        @close="closeInvoiceExportModal"
      />

      <PayModal
        :show="showPayInvoiceModal"
        :row="payInvoiceRow"
        :submitting="payInvoiceSubmitting"
        @close="closePayInvoiceModal"
        @submit="submitPayInvoiceModal"
      />

      <DocumentsDownloadModal
        :show="showDownloadModal"
        :labels="text.downloadModal"
        :context="downloadScopeContext"
        :latest-request="latestDownloadRequest"
        :package-limit="downloadPackageLimit"
        :expires-hours="downloadExpiresHours"
        :submitting="downloadRequestSubmitting"
        @close="closeDownloadModal"
        @refresh="refreshLatestDownloadRequest"
        @create="createDownloadRequest"
        @download-package="downloadRequestPackage"
        @retry-package="retryDownloadRequestPackage"
        @regenerate="regenerateDownloadRequest"
      />
    </template>
  </CommonPageShell>
</template>

<script>
import CommonPageShell from '@/components/common/CommonPageShell.vue';
import DocumentsDownloadModal from '@/components/common/downloads/DocumentsDownloadModal.vue';
import InvoicesToolbar from '@/components/invoices/InvoicesToolbar.vue';
import InvoicesTable from '@/components/invoices/InvoicesTable.vue';
import InvoicesKpiCard from '@/components/invoices/InvoicesKpiCard.vue';
import StatusTabs from '@/components/common/StatusTabs.vue';
import PayModal from '@/components/pay/modal/PayModal.vue';
import ExportFormatModal from '@/components/common/exports/ExportFormatModal.vue';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  createDefaultInvoicesToolbarFilters,
  normalizeInvoicesToolbarFilters,
  filterInvoicesRowsByToolbar,
} from '@/composables/invoices/useInvoicesFilters';
import {
  buildInvoiceStatusTabs,
  filterInvoicesByStatus,
} from '@/composables/invoices/useInvoicesStatusTabs';
import {
  buildInvoicesKpiCards,
  createInitialInvoicesMetricsValues,
  mapInvoicesMetricsPayload,
} from '@/composables/invoices/useInvoicesMetrics';
import {
  createDefaultInvoicesFilterOptions,
  buildInvoicesListParams,
  fetchInvoiceBusinessFilterOptions,
  fetchInvoicesFilterOptions,
  fetchInvoicesSnapshot,
} from '@/composables/invoices/useInvoicesList';
import invoicesService from '@/services/invoices';
import paymentRepository from '@/services/paymentRepository';
import { extractApiErrorMessage, extractApiSuccessMessage, extractPayload } from '@/utils/dataNormalization';
import { downloadBlobFile } from '@/utils/fileDownload';
import {
  resolveTotalPages,
  ensureValidPage,
  sliceRowsByPage,
  buildPaginationPayload,
} from '@/composables/shared/usePagination';
import { resolveSortChange, sortRows } from '@/composables/shared/useSorting';
import { formatDateShort } from '@/utils/datePresentation';

const { catalog: invoiceText } = useDomainText('invoices');
const DOWNLOAD_REQUEST_POLLING_MS = 15000;

export default {
  name: 'InvoicesPage',
  components: {
    CommonPageShell,
    DocumentsDownloadModal,
    InvoicesToolbar,
    InvoicesTable,
    InvoicesKpiCard,
    StatusTabs,
    PayModal,
    ExportFormatModal,
  },
  data() {
    return {
      text: invoiceText,
      isInvoiceExportModalOpen: false,
      tableLoading: false,
      invoiceRows: [],
      filterOptions: createDefaultInvoicesFilterOptions(),
      toolbarSearch: '',
      toolbarFilters: createDefaultInvoicesToolbarFilters(),
      appliedToolbarSearch: '',
      appliedToolbarFilters: createDefaultInvoicesToolbarFilters(),
      activeStatusTab: 'all',
      metricsValues: createInitialInvoicesMetricsValues(),
      currentPage: 1,
      pageSize: 20,
      sortField: 'date',
      sortDirection: 'desc',
      listRequestToken: 0,
      showPayInvoiceModal: false,
      payInvoiceSubmitting: false,
      payInvoiceRow: null,
      showDownloadModal: false,
      downloadRequestSubmitting: false,
      latestDownloadRequest: null,
      downloadPackageLimit: 200,
      downloadExpiresHours: 24,
      downloadRequestPollingId: null,
    };
  },
  computed: {
    exportService() {
      return invoicesService;
    },
    headerActions() {
      return [ 
        {
          key: 'invoice',
          label: this.text.headerActions.invoice,
          icon: 'fa-file-text-o',
          variant: 'success',
        },
        {
          key: 'export',
          label: this.text.headerActions.exportExcel,
          icon: 'fa-file-excel-o',
          variant: 'secondary',
          className: 'app-header-btn--export',
        },
        {
          key: 'download',
          label: this.text.headerActions.exportDownload,
          icon: 'fa-download',
          variant: 'secondary',
          className: 'app-header-btn--download-list',
        },
      ];
    },
    toolbarFilteredRows() {
      return filterInvoicesRowsByToolbar(this.invoiceRows, {
        search: this.appliedToolbarSearch,
        filters: this.appliedToolbarFilters,
      });
    },
    statusTabs() {
      return buildInvoiceStatusTabs(this.toolbarFilteredRows, this.text.tabs || {});
    },
    statusFilteredRows() {
      return filterInvoicesByStatus(this.toolbarFilteredRows, this.activeStatusTab);
    },
    sortedRows() {
      return sortRows(this.statusFilteredRows, {
        field: this.sortField,
        direction: this.sortDirection,
      }, {
        date: (row) => new Date(row?.date || 0).getTime(),
        customer: (row) => String(row?.customer_name || row?.company_name || '').trim().toLowerCase(),
        business: (row) => String(row?.sale_name || row?.business_name || '').trim().toLowerCase(),
        subtotal: (row) => Number(row?.subtotal || 0),
        total: (row) => Number(row?.total || 0),
        balance: (row) => Number(row?.balance || 0),
      });
    },
    totalPages() {
      return resolveTotalPages(this.sortedRows.length, this.pageSize);
    },
    paginatedRows() {
      return sliceRowsByPage(this.sortedRows, this.currentPage, this.pageSize);
    },
    currentPageInvoiceIds() {
      return (Array.isArray(this.paginatedRows) ? this.paginatedRows : [])
        .map((row) => Number(row?.id || 0))
        .filter((id) => id > 0);
    },
    exportFilters() {
      return {
        ...(this.appliedToolbarFilters || {}),
        status_tab: String(this.activeStatusTab || 'all').trim() || 'all',
      };
    },
    invoicesPagination() {
      return buildPaginationPayload({
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        totalItems: this.sortedRows.length,
      });
    },
    kpiCards() {
      return buildInvoicesKpiCards(this.metricsValues, this.text.metrics || {});
    },
    kpiGridStyle() {
      const totalCards = Array.isArray(this.kpiCards) ? this.kpiCards.length : 0;
      const columns = Math.min(Math.max(totalCards, 1), 5);

      return {
        '--invoices-kpi-columns': String(columns),
      };
    },
    activeStatusTabLabel() {
      const activeTab = (Array.isArray(this.statusTabs) ? this.statusTabs : [])
        .find((tab) => String(tab?.key || '') === String(this.activeStatusTab || 'all'));

      return String(activeTab?.label || this.text?.tabs?.all || '').trim();
    },
    downloadScopeLabel() {
      const parts = ['Facturas'];
      if (this.activeStatusTabLabel !== '') {
        parts.push(this.activeStatusTabLabel);
      }

      const dateFrom = String(this.appliedToolbarFilters?.date_from || '').trim();
      const dateTo = String(this.appliedToolbarFilters?.date_to || '').trim();
      if (dateFrom !== '' || dateTo !== '') {
        const dateFromLabel = dateFrom !== '' ? formatDateShort(dateFrom, { fallback: dateFrom }) : '...';
        const dateToLabel = dateTo !== '' ? formatDateShort(dateTo, { fallback: dateTo }) : '...';
        parts.push(`${dateFromLabel} - ${dateToLabel}`);
      }

      return parts.join(' · ');
    },
    downloadScopeContext() {
      return {
        totalInvoices: this.statusFilteredRows.length,
        scopeDescription: String(this.text?.downloadModal?.contextMessage || '').trim(),
        scopeLabel: this.downloadScopeLabel,
      };
    },
  },
  mounted() {
    this.loadInvoices();
    this.loadFilterOptions();
  },
  beforeUnmount() {
    this.stopDownloadRequestPolling();
  },
  methods: {
    buildListParams() {
      return buildInvoicesListParams({
        toolbarSearch: this.appliedToolbarSearch,
        toolbarFilters: this.appliedToolbarFilters,
      });
    },
    async loadInvoices() {
      const requestToken = ++this.listRequestToken;
      this.tableLoading = true;

      try {
        const snapshot = await fetchInvoicesSnapshot(this.buildListParams());

        if (requestToken === this.listRequestToken) {
          const rows = Array.isArray(snapshot?.rows) ? snapshot.rows : [];
          this.invoiceRows = Array.isArray(rows) ? rows : [];
          this.metricsValues = mapInvoicesMetricsPayload(snapshot?.metrics || {});
          this.currentPage = 1;
        }
      } catch (error) {
        if (requestToken === this.listRequestToken) {
          this.invoiceRows = [];
          this.metricsValues = createInitialInvoicesMetricsValues();
        }
      } finally {
        if (requestToken === this.listRequestToken) {
          this.tableLoading = false;
        }
      }
    },
    async loadFilterOptions() {
      try {
        const nextOptions = await fetchInvoicesFilterOptions();
        this.filterOptions = {
          ...createDefaultInvoicesFilterOptions(),
          ...(nextOptions || {}),
        };
      } catch (error) {
        this.filterOptions = createDefaultInvoicesFilterOptions();
      }
    },
    normalizeIdSelection(values = []) {
      return [...new Set(
        (Array.isArray(values) ? values : [])
          .map((value) => Number(value || 0) || 0)
          .filter((value) => value > 0)
      )].sort((left, right) => left - right);
    },
    hasSameIdSelection(left = [], right = []) {
      const normalizedLeft = this.normalizeIdSelection(left);
      const normalizedRight = this.normalizeIdSelection(right);

      if (normalizedLeft.length !== normalizedRight.length) {
        return false;
      }

      return normalizedLeft.every((value, index) => value === normalizedRight[index]);
    },
    async loadBusinessFilterOptions(customerIds = []) {
      const businessOptions = await fetchInvoiceBusinessFilterOptions({ customerIds });
      const allowedBusinessIds = new Set(
        (Array.isArray(businessOptions) ? businessOptions : [])
          .map((option) => String(option?.value || '').trim())
          .filter((value) => value !== '')
      );
      const normalizeBusinessIds = (values = []) => {
        return (Array.isArray(values) ? values : [])
          .map((value) => String(value || '').trim())
          .filter((value) => value !== '' && allowedBusinessIds.has(value));
      };

      const nextToolbarBusinessIds = normalizeBusinessIds(this.toolbarFilters?.business_ids);
      const nextAppliedBusinessIds = normalizeBusinessIds(this.appliedToolbarFilters?.business_ids);

      this.filterOptions = {
        ...this.filterOptions,
        business: businessOptions,
      };

      this.toolbarFilters = {
        ...this.toolbarFilters,
        business_ids: nextToolbarBusinessIds,
      };

      this.appliedToolbarFilters = {
        ...this.appliedToolbarFilters,
        business_ids: nextAppliedBusinessIds,
      };
    },
    onHeaderAction(action) {
      const actionKey = String(action?.key || '').trim();

      if (actionKey === 'invoice') {
        this.$router.push({ name: 'invoiceCreate' });
        return;
      }

      if (actionKey === 'export') {
        this.isInvoiceExportModalOpen = true;
        return;
      }

      if (actionKey === 'download') {
        this.openDownloadModal();
      }
    },
    closeInvoiceExportModal() {
      this.isInvoiceExportModalOpen = false;
    },
    normalizeLatestDownloadRequest(payload) {
      const safePayload = payload && typeof payload === 'object' ? payload : {};
      const request = safePayload?.request && typeof safePayload.request === 'object'
        ? safePayload.request
        : null;

      this.downloadPackageLimit = Number(safePayload?.package_limit || this.downloadPackageLimit || 200) || 200;
      this.downloadExpiresHours = Number(safePayload?.expires_hours || this.downloadExpiresHours || 24) || 24;
      this.latestDownloadRequest = request;
    },
    async refreshLatestDownloadRequest() {
      try {
        const response = await invoicesService.downloadRequestsLatest({}, { skipLoading: true });
        const payload = extractPayload(response);
        this.normalizeLatestDownloadRequest(payload);
      } catch (error) {
        // Keep modal usable even when latest request cannot be loaded.
      }

      this.syncDownloadRequestPolling();
    },
    openDownloadModal() {
      this.showDownloadModal = true;
      this.refreshLatestDownloadRequest();
    },
    closeDownloadModal() {
      this.showDownloadModal = false;
      this.stopDownloadRequestPolling();
    },
    hasActiveDownloadProcessing() {
      const status = String(this.latestDownloadRequest?.status || '').trim().toLowerCase();
      return status === 'pending' || status === 'processing';
    },
    stopDownloadRequestPolling() {
      if (this.downloadRequestPollingId) {
        window.clearInterval(this.downloadRequestPollingId);
        this.downloadRequestPollingId = null;
      }
    },
    syncDownloadRequestPolling() {
      const shouldPoll = this.showDownloadModal && this.hasActiveDownloadProcessing();
      if (!shouldPoll) {
        this.stopDownloadRequestPolling();
        return;
      }

      if (this.downloadRequestPollingId) {
        return;
      }

      this.downloadRequestPollingId = window.setInterval(() => {
        if (!this.showDownloadModal) {
          this.stopDownloadRequestPolling();
          return;
        }

        this.refreshLatestDownloadRequest();
      }, DOWNLOAD_REQUEST_POLLING_MS);
    },
    buildDownloadRequestPayload(documentType = 'both', groupBy = 'none') {
      return {
        document_type: String(documentType || 'both').trim().toLowerCase(),
        group_by: String(groupBy || 'none').trim().toLowerCase() || 'none',
        tab_key: String(this.activeStatusTab || 'all').trim().toLowerCase() || 'all',
        tab_label: this.activeStatusTabLabel,
        scope_label: this.downloadScopeLabel,
        search: String(this.appliedToolbarSearch || '').trim(),
        list_params: this.buildListParams(),
      };
    },
    resolveDownloadFileNameFromHeaders(headers = {}, fallback = 'facturas_paquete_1_de_1.zip') {
      const contentDisposition = String(headers?.['content-disposition'] || headers?.['Content-Disposition'] || '').trim();
      if (contentDisposition === '') {
        return fallback;
      }

      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      if (utf8Match && utf8Match[1]) {
        try {
          return decodeURIComponent(String(utf8Match[1]).trim());
        } catch (error) {
          return String(utf8Match[1]).trim() || fallback;
        }
      }

      const basicMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      return String(basicMatch?.[1] || '').trim() || fallback;
    },
    async createDownloadRequest(payload = {}) {
      if (this.downloadRequestSubmitting) {
        return;
      }

      this.downloadRequestSubmitting = true;
      const requestPayload = this.buildDownloadRequestPayload(
        payload?.documentType || 'both',
        payload?.groupBy || 'none',
      );
      const totalInvoices = Number(this.statusFilteredRows.length || 0) || 0;
      const isSinglePackageScope = totalInvoices > 0 && totalInvoices <= Number(this.downloadPackageLimit || 200);

      try {
        if (isSinglePackageScope) {
          const response = await invoicesService.downloadRequestsDirectDownload(requestPayload);
          const fileName = this.resolveDownloadFileNameFromHeaders(response?.headers || {}, 'facturas_paquete_1_de_1.zip');
          downloadBlobFile(response?.data ?? response, fileName, 'application/zip');
          await this.refreshLatestDownloadRequest();
          this.closeDownloadModal();
          return;
        }

        const response = await invoicesService.downloadRequestsStore(requestPayload);
        const apiErrorMessage = extractApiErrorMessage(response, '');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.normalizeLatestDownloadRequest(extractPayload(response));
        this.$toast?.success(String(this.text?.toasts?.downloadRequestCreated || ''));
      } catch (error) {
        const fallback = String(this.text?.toasts?.downloadRequestError || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      } finally {
        this.downloadRequestSubmitting = false;
        this.syncDownloadRequestPolling();
      }
    },
    async regenerateDownloadRequest(payload = {}) {
      const requestId = Number(this.latestDownloadRequest?.id || 0) || 0;
      if (requestId <= 0 || this.downloadRequestSubmitting) {
        return;
      }

      this.downloadRequestSubmitting = true;

      try {
        const response = await invoicesService.downloadRequestsRegenerate(requestId, {
          document_type: String(payload?.documentType || 'both').trim().toLowerCase(),
          group_by: String(payload?.groupBy || 'none').trim().toLowerCase() || 'none',
        });
        const apiErrorMessage = extractApiErrorMessage(response, '');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.normalizeLatestDownloadRequest(extractPayload(response));
        this.$toast?.success(String(this.text?.toasts?.regenerateSuccess || ''));
      } catch (error) {
        const fallback = String(this.text?.toasts?.regenerateError || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      } finally {
        this.downloadRequestSubmitting = false;
        this.syncDownloadRequestPolling();
      }
    },
    resolveDownloadPackageFileName(packageRow = {}) {
      const rawName = String(packageRow?.file_name || '').trim();
      if (rawName !== '') {
        return rawName;
      }

      const index = Number(packageRow?.package_index || 1) || 1;
      const total = Number(this.latestDownloadRequest?.total_packages || 1) || 1;
      return `facturas_paquete_${index}_de_${total}.zip`;
    },
    async downloadRequestPackage(packageRow = {}) {
      const requestId = Number(this.latestDownloadRequest?.id || 0) || 0;
      const packageId = Number(packageRow?.id || 0) || 0;
      if (requestId <= 0 || packageId <= 0 || this.downloadRequestSubmitting) {
        return;
      }

      this.downloadRequestSubmitting = true;

      try {
        const response = await invoicesService.downloadRequestsDownloadPackage(requestId, packageId);
        const fileName = this.resolveDownloadPackageFileName(packageRow);
        downloadBlobFile(response?.data ?? response, fileName, 'application/zip');
        await this.refreshLatestDownloadRequest();
      } catch (error) {
        const fallback = String(this.text?.toasts?.downloadPackageError || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
        await this.refreshLatestDownloadRequest();
      } finally {
        this.downloadRequestSubmitting = false;
      }
    },
    async retryDownloadRequestPackage(packageRow = {}) {
      const requestId = Number(this.latestDownloadRequest?.id || 0) || 0;
      const packageId = Number(packageRow?.id || 0) || 0;
      if (requestId <= 0 || packageId <= 0 || this.downloadRequestSubmitting) {
        return;
      }

      this.downloadRequestSubmitting = true;

      try {
        const response = await invoicesService.downloadRequestsRetryPackage(requestId, packageId);
        const apiErrorMessage = extractApiErrorMessage(response, '');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.normalizeLatestDownloadRequest(extractPayload(response));
        this.$toast?.success(String(this.text?.toasts?.retryPackageSuccess || ''));
      } catch (error) {
        const fallback = String(this.text?.toasts?.retryPackageError || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      } finally {
        this.downloadRequestSubmitting = false;
        this.syncDownloadRequestPolling();
      }
    },
    handleToolbarSearch(searchValue) {
      const nextSearch = String(searchValue || '');
      this.toolbarSearch = nextSearch;
      this.appliedToolbarSearch = nextSearch;
      this.currentPage = 1;
    },
    async handleToolbarFilters(filters) {
      const previousDateStart = String(this.toolbarFilters?.date_from || '').trim();
      const previousDateEnd = String(this.toolbarFilters?.date_to || '').trim();
      const previousCustomerIds = Array.isArray(this.toolbarFilters?.customer_ids)
        ? this.toolbarFilters.customer_ids
        : [];
      const normalizedFilters = normalizeInvoicesToolbarFilters(filters);

      this.toolbarFilters = normalizedFilters;

      const customerChanged = !this.hasSameIdSelection(previousCustomerIds, normalizedFilters.customer_ids);
      if (customerChanged) {
        await this.loadBusinessFilterOptions(normalizedFilters.customer_ids);
      }

      const nextDateStart = String(normalizedFilters?.date_from || '').trim();
      const nextDateEnd = String(normalizedFilters?.date_to || '').trim();
      const dateChanged = nextDateStart !== previousDateStart || nextDateEnd !== previousDateEnd;

      if (dateChanged) {
        this.appliedToolbarSearch = String(this.toolbarSearch || '').trim();
        this.appliedToolbarFilters = normalizedFilters;
        this.loadInvoices();
      }
    },
    async applyToolbarFilters(payload) {
      const nextSearch = String(payload?.search ?? this.toolbarSearch ?? '').trim();
      const nextFilters = normalizeInvoicesToolbarFilters(payload?.filters ?? this.toolbarFilters);
      const customerChanged = !this.hasSameIdSelection(this.toolbarFilters?.customer_ids, nextFilters.customer_ids);

      if (customerChanged) {
        await this.loadBusinessFilterOptions(nextFilters.customer_ids);
      }

      this.appliedToolbarSearch = nextSearch;
      this.appliedToolbarFilters = nextFilters;
      this.loadInvoices();
    },
    selectStatusTab(tabKey) {
      this.activeStatusTab = String(tabKey || 'all').trim() || 'all';
      this.currentPage = 1;
    },
    handlePageChange(nextPage) {
      this.currentPage = ensureValidPage(nextPage, this.totalPages);
    },
    handleSortChange(payload) {
      const sort = resolveSortChange({
        currentField: this.sortField,
        currentDirection: this.sortDirection,
        requestedField: payload?.field,
        requestedDirection: payload?.direction,
      });

      this.sortField = sort.field;
      this.sortDirection = sort.direction;
      this.currentPage = 1;
    },
    viewInvoice(row) {
      const invoiceId = Number(row?.id || 0) || 0;
      if (invoiceId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        return;
      }

      try {
        sessionStorage.setItem('app.breadcrumb.invoice-detail', JSON.stringify({
          contextId: String(invoiceId),
          source: 'invoices-list',
          saleId: Number(row?.sale_id || 0) || 0,
          saleName: String(row?.sale_name || row?.business_name || '').trim(),
          customerId: Number(row?.customer_id || 0) || 0,
          customerName: String(row?.customer_name || row?.company_name || '').trim(),
        }));
      } catch (error) {
        // Keep navigation flow even if session storage is unavailable.
      }

      this.$router.push({
        name: 'invoiceDetail',
        params: {
          invoiceId: String(invoiceId),
        },
        state: {
          invoiceSnapshot: row && typeof row === 'object' ? { ...row } : null,
        },
      });
    },
    hasInvoicePaymentMethodData(row = {}) {
      const paymentMethod = row?.invoice_billing_data?.payment_method;
      const paymentMethodCode = String(
        paymentMethod?.code
        || row?.invoice_payment_method
        || row?.payment_method
        || row?.paymentMethod
        || ''
      ).trim();
      const paymentMethodLabel = String(
        paymentMethod?.label
        || row?.invoice_payment_method_label
        || row?.payment_method_label
        || row?.paymentMethodLabel
        || ''
      ).trim();

      return paymentMethodCode !== '' || paymentMethodLabel !== '';
    },
    async resolveInvoiceRowForPayModal(row = {}) {
      const invoiceId = Number(row?.id || 0) || 0;
      if (invoiceId <= 0) {
        return row;
      }

      if (this.hasInvoicePaymentMethodData(row)) {
        return row;
      }

      try {
        const response = await invoicesService.show(invoiceId, {}, { skipLoading: true });
        const payload = extractPayload(response);
        const detailRow = payload && typeof payload === 'object'
          ? (payload?.data && typeof payload.data === 'object' ? payload.data : payload)
          : null;

        if (detailRow && typeof detailRow === 'object') {
          return {
            ...row,
            ...detailRow,
          };
        }
      } catch (error) {
        // Keep fallback flow with list row when detail request fails.
      }

      return row;
    },
    async payInvoice(row) {
      const resolvedRow = await this.resolveInvoiceRowForPayModal(row);
      const modalRow = this.buildPayInvoiceModalRow(resolvedRow);
      if (!modalRow) {
        this.$toast?.error('No se pudo identificar el plan de pago para esta factura.');
        return;
      }

      this.payInvoiceRow = modalRow;
      this.showPayInvoiceModal = true;
    },
    buildPayInvoiceModalRow(row = {}) {
      const sourceBillingData = row?.invoice_billing_data && typeof row.invoice_billing_data === 'object'
        ? row.invoice_billing_data
        : {};
      const sourcePaymentMethod = sourceBillingData?.payment_method && typeof sourceBillingData.payment_method === 'object'
        ? sourceBillingData.payment_method
        : {};
      const invoicePaymentMethodLabel = String(
        sourcePaymentMethod?.label
        || row?.paymentMethodLabel
        || row?.payment_method_label
        || row?.invoice_payment_method_label
        || ''
      ).trim();
      const invoicePaymentMethodCode = String(
        sourcePaymentMethod?.code
        || row?.paymentMethod
        || row?.payment_method
        || row?.invoice_payment_method
        || ''
      ).trim();
      const paymentId = Number(row?.payment_id || row?.payment?.id || 0) || 0;
      const customerId = Number(row?.customer_id || row?.company_id || 0) || 0;
      const total = Number(row?.balance || row?.total || 0) || 0;

      if (paymentId <= 0 || customerId <= 0 || total <= 0) {
        return null;
      }

      return {
        id: paymentId,
        customer_id: customerId,
        company_id: customerId,
        customer_name: String(row?.customer_name || row?.company_name || '').trim(),
        company_name: String(row?.company_name || row?.customer_name || '').trim(),
        date: String(row?.date || '').trim(),
        currency: String(row?.currency || 'MXN').trim().toUpperCase() || 'MXN',
        exchange_rate: Number(row?.exchange_rate || 1) || 1,
        total,
        subtotal: total,
        invoice_id: Number(row?.id || 0) || 0,
        invoice: `${String(row?.serial || '').trim()}${String(row?.folio || '').trim()}`.trim(),
        voucher_type: 'cfdi',
        special: 0,
        contributor_id: Number(row?.invoice_contributor_id || 0) || 0,
        invoice_contributor_id: Number(row?.invoice_contributor_id || 0) || 0,
        invoice_payment_method_label: invoicePaymentMethodLabel,
        invoice_payment_method: invoicePaymentMethodCode,
        invoice_billing_data: {
          contributor: {
            id: Number(row?.invoice_contributor_id || 0) || 0,
            rfc: String(row?.invoice_receiver_rfc || '').trim(),
          },
          payment_method: {
            label: invoicePaymentMethodLabel,
            code: invoicePaymentMethodCode,
          },
        },
      };
    },
    resolveCreatedPaymentReceiptId(response) {
      const payload = extractPayload(response);

      const id = Number(
        payload?.payment_receipt?.id
        || payload?.payment_receipt_id
        || 0
      ) || 0;

      return id > 0 ? id : 0;
    },
    closePayInvoiceModal(force = false) {
      if (this.payInvoiceSubmitting && !force) {
        return;
      }

      this.showPayInvoiceModal = false;
      this.payInvoiceRow = null;
    },
    async submitPayInvoiceModal(payload) {
      this.payInvoiceSubmitting = true;

      try {
        const paymentPlanId = Number(payload?.payment_plan_id || 0);
        if (paymentPlanId <= 0) {
          this.$toast?.error('No se pudo identificar el plan de pago para registrar el cobro.');
          return;
        }

        const response = await paymentRepository.payPlan(paymentPlanId, payload);
        const apiErrorMessage = extractApiErrorMessage(response, 'No fue posible registrar el pago de la factura.');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, 'Pago de factura registrado correctamente.'));
        const createdPaymentReceiptId = this.resolveCreatedPaymentReceiptId(response);
        this.closePayInvoiceModal(true);

        if (createdPaymentReceiptId > 0) {
          this.$router.push({
            name: 'paymentReceiptDetail',
            params: {
              paymentReceiptId: String(createdPaymentReceiptId),
            },
          }).catch(() => {});
          return;
        }

        await this.loadInvoices();
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || error?.message || 'No fue posible registrar el pago de la factura.');
      } finally {
        this.payInvoiceSubmitting = false;
      }
    },
    async downloadXml(row) {
      const invoiceId = Number(row?.id || 0) || 0;
      if (invoiceId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        return;
      }

      try {
        const response = await invoicesService.downloadXml(invoiceId);
        const fileBaseName = this.resolveInvoiceDocumentBaseName(row);
        downloadBlobFile(response?.data ?? response, `${fileBaseName}.xml`, 'application/xml');
      } catch (error) {
        const fallback = String(this.text?.toasts?.downloadXml(this.resolveInvoiceReference(row)) || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      }
    },
    async downloadFiles(row) {
      const invoiceId = Number(row?.id || 0) || 0;
      if (invoiceId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        return;
      }

      try {
        const response = await invoicesService.preview(invoiceId);
        this.openPdfBlob(response?.data ?? response, row);
      } catch (error) {
        const fallback = String(this.text?.toasts?.downloadFiles(this.resolveInvoiceReference(row)) || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      }
    },
    async downloadPdf(row) {
      const invoiceId = Number(row?.id || 0) || 0;
      if (invoiceId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        return;
      }

      try {
        const response = await invoicesService.downloadPdf(invoiceId);
        const fileBaseName = this.resolveInvoiceDocumentBaseName(row);
        downloadBlobFile(response?.data ?? response, `${fileBaseName}.pdf`, 'application/pdf');
      } catch (error) {
        const fallback = String(this.text?.toasts?.downloadPdf(this.resolveInvoiceReference(row)) || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      }
    },
    uploadReceipt(row) {
      const reference = this.resolveInvoiceReference(row);
      const formatter = this.text?.toasts?.uploadReceipt;
      this.$toast?.info(typeof formatter === 'function' ? formatter(reference) : '');
    },
    cloneInvoice(row) {
      const invoiceId = Number(row?.id || 0) || 0;
      if (invoiceId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        return;
      }

      this.$router.push({
        name: 'invoiceCreate',
        query: {
          clone_invoice_id: String(invoiceId),
        },
        state: {
          cloneInvoiceSnapshot: row && typeof row === 'object' ? { ...row } : null,
        },
      }).catch(() => {});
    },
    openPdfBlob(payload, row = null) {
      const blob = payload instanceof Blob
        ? payload
        : new Blob([payload], { type: 'application/pdf' });

      const objectUrl = window.URL.createObjectURL(blob);
      const opened = window.open(objectUrl, '_blank', 'noopener');

      if (!opened) {
        downloadBlobFile(blob, `${this.resolveInvoiceDocumentBaseName(row || {})}.pdf`, 'application/pdf');
      }

      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 60 * 1000);
    },
    resolveInvoiceDocumentBaseName(row) {
      const serial = String(row?.serial || '').trim();
      const folio = String(row?.folio || '').trim();
      const uuid = String(row?.uuid || '').trim().toUpperCase();
      const reference = `${serial}${folio}`.trim() || this.resolveInvoiceReference(row);

      const safeReference = reference
        .replace(/\s+/g, '_')
        .replace(/[^A-Za-z0-9_\-]/g, '') || 'factura';

      const safeUuid = uuid.replace(/[^A-Z0-9\-]/g, '');
      return safeUuid !== '' ? `${safeReference}_${safeUuid}` : safeReference;
    },
    resolveInvoiceReference(row) {
      const fallback = String(this.text?.toasts?.invoiceReferenceFallback || '').trim();
      const serial = String(row?.serial || '').trim();
      const folio = String(row?.folio || '').trim();
      return `${serial}${folio}`.trim() || fallback;
    },
  },
};
</script>

<style scoped>
.invoices-kpis-grid {
  grid-template-columns: repeat(var(--invoices-kpi-columns, 4), minmax(0, 1fr));
}

@media (max-width: 1500px) {
  .invoices-kpis-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .invoices-kpis-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .invoices-kpis-grid {
    grid-template-columns: 1fr;
  }
}
</style>
